








































































































































































import LazyHydrate from 'vue-lazy-hydration';
import {
  SfAddToCart,
  SfButton,
  SfColor,
  SfGallery,
  SfHeading,
  SfLoader,
  SfPrice,
  SfRating,
  SfSelect,
  SfAlert,
} from '@storefront-ui/vue';
import {
  ref,
  computed,
  watch,
  useContext,
  useRoute,
  useRouter,
  defineComponent,
  PropType, toRef,
} from '@nuxtjs/composition-api';

import {
  getSwatchData as getProductSwatchData,
  getName as getProductName,
} from '~/modules/catalog/product/getters/productGetters';

import {
  getTotalReviews,
  getAverageRating,
} from '~/modules/review/getters/reviewGetters';

import { useCart } from '~/modules/checkout/composables/useCart';
import useWishlist from '~/modules/wishlist/composables/useWishlist';
import SvgImage from '~/components/General/SvgImage.vue';
import HTMLContent from '~/components/HTMLContent.vue';
import AddToWishlist from '~/components/AddToWishlist.vue';
import { useUser } from '~/modules/customer/composables/useUser';
import { getConfigurableProductPriceCommand } from '~/modules/catalog/pricing/getConfigurableProductPriceCommand';
import { getConfigurableProductSpecialPriceCommand } from '~/modules/catalog/pricing/getConfigurableProductSpecialPriceCommand';
import { Product } from '~/modules/catalog/product/types';
import ProductTabs from '~/modules/catalog/product/components/tabs/ProductTabs.vue';
import { useProductGallery } from '~/modules/catalog/product/composables/useProductGallery';
import { TabsConfig, useProductTabs } from '~/modules/catalog/product/composables/useProductTabs';
import { usePageStore } from '~/stores/page';

export default defineComponent({
  name: 'ConfigurableProduct',
  components: {
    HTMLContent,
    LazyHydrate,
    SfAddToCart,
    SfButton,
    SfColor,
    SfGallery,
    SfHeading,
    SfLoader,
    SfPrice,
    SfRating,
    SfSelect,
    SfAlert,
    AddToWishlist,
    SvgImage,
    ProductTabs,
  },
  transition: 'fade',
  props: {
    product: {
      type: [Object, null] as PropType<Product>,
      default: null,
    },
    isFetching: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const qty = ref(1);
    const product = toRef(props, 'product');
    const route = useRoute();
    const router = useRouter();
    const { routeData } = usePageStore();
    const {
      addItem, error: cartError, loading: isCartLoading, canAddToCart,
    } = useCart();
    const { productGallery, imageSizes } = useProductGallery(product);
    const { activeTab, setActiveTab, openNewReviewTab } = useProductTabs();

    const { isAuthenticated } = useUser();
    const { addOrRemoveItem, isInWishlist } = useWishlist();
    const { app } = useContext();

    const productShortDescription = computed(
      () => props.product?.short_description?.html || '',
    );

    const configurableOptions = computed(
      () => props.product?.configurable_options ?? [],
    );

    const productConfiguration = ref({ ...route.value.query });
    const productPrice = computed(() => getConfigurableProductPriceCommand(props.product));
    const productSpecialPrice = computed(() => getConfigurableProductSpecialPriceCommand(props.product));

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      },
      configurations: Object.entries(productConfiguration.value)
        .map((config) => config[1])
        .filter(Boolean) as string[],
    });

    const totalReviews = computed(() => getTotalReviews(props.product));
    const averageRating = computed(() => getAverageRating(props.product));
    const addToCartError = computed(() => cartError.value?.addItem?.message);

    const syncConfigurationToUrl = (replace = false) => {
      if (!process.client) return;

      const routerData = router.resolve({
        path: `${app.localePath(window.location.pathname)}`,
        query: {
          ...productConfiguration.value,
        },
      });

      if (replace) {
        window.history.replaceState({}, null, routerData.href);
      } else {
        window.history.pushState({}, null, routerData.href);
      }
    };

    const updateProductConfiguration = (label: string, value: string) => {
      if (productConfiguration.value[label] === value) return;

      productConfiguration.value = {
        ...productConfiguration.value,
        [label]: value,
      };
      syncConfigurationToUrl(false);
      emit('fetchProduct', { query: getBaseSearchQuery() });
    };

    // Preselect first available value per option (color, size, …) when not set via URL
    const applyDefaultConfiguration = () => {
      const options = configurableOptions.value;
      if (!options?.length) return;

      const next = { ...productConfiguration.value };
      let changed = false;

      options.forEach((option) => {
        const attrUid = option.attribute_uid;
        const firstValueUid = option.values?.[0]?.uid;
        if (!attrUid || !firstValueUid) return;
        if (next[attrUid]) return;

        next[attrUid] = firstValueUid;
        changed = true;
      });

      if (!changed) return;

      productConfiguration.value = next;
      syncConfigurationToUrl(true);
      emit('fetchProduct', { query: getBaseSearchQuery() });
    };

    watch(
      () => [props.product?.uid, configurableOptions.value?.length],
      () => {
        applyDefaultConfiguration();
      },
      { immediate: true },
    );

    return {
      addItem,
      addItemToWishlist: addOrRemoveItem,
      canAddToCart,
      configurableOptions,
      updateProductConfiguration,
      isAuthenticated,
      isInWishlist: computed(() => isInWishlist({ product: props.product })),
      isCartLoading,
      productConfiguration,
      productGallery,
      getProductName,
      getProductSwatchData,
      productPrice,
      productShortDescription,
      productSpecialPrice,
      qty,
      totalReviews,
      averageRating,
      imageSizes,
      setActiveTab,
      openNewReviewTab,
      activeTab,
      TabsConfig,
      addToCartError,
    };
  },
});
